<script setup>
import { computed, ref, nextTick } from 'vue';
import McpQuickStart from './McpQuickStart.vue';
import { useClipboard } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';

defineProps({ accountId: { type: [String, Number], required: true } });
const connectionDetails = ref(null);
const openConnection = async () => {
  connectionDetails.value.open = true;
  await nextTick();
  connectionDetails.value.scrollIntoView?.({
    block: 'start',
    behavior: 'smooth',
  });
};
const server = window.globalConfig?.BANK_MCP || {};
const { copy } = useClipboard();
const configuration = computed(() =>
  JSON.stringify(
    {
      mcpServers: {
        'talkvia-bancos': {
          url: server.url,
          headers: {
            Authorization: 'Bearer SEU_TOKEN_TALKVIA',
            'X-Talkvia-Environment': server.environment,
          },
        },
      },
    },
    null,
    2
  )
);
const copyConfiguration = async () => {
  try {
    await copy(configuration.value);
    useAlert(
      'Configuração MCP copiada. Substitua SEU_TOKEN_TALKVIA pelo seu token.'
    );
  } catch {
    useAlert(
      'Não foi possível copiar. Selecione e copie a configuração abaixo.'
    );
  }
};
</script>

<!-- API documentation is maintained in Brazilian Portuguese, like its parent page. -->
<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <section
    class="mcp-guide flex min-w-0 flex-col gap-6 rounded-xl border border-n-weak p-5"
  >
    <div>
      <h2 class="text-lg font-semibold text-n-slate-12">MCP dos bancos</h2>
      <p class="mt-2 text-sm text-n-slate-11">
        Conecte seu cliente MCP aos bancos integrados usando o token pessoal da
        Talkvia, disponível em Meu perfil. O acesso respeita as permissões do
        usuário e as integrações da conta.
      </p>
    </div>
    <McpQuickStart @configure="openConnection" />
    <h3 class="text-sm font-semibold text-n-slate-12">
      Explore os detalhes quando precisar
    </h3>
    <details class="guide-section">
      <summary>O que é MCP, em palavras simples?</summary>
      <div class="guide-content">
        <p>
          MCP significa Model Context Protocol. É um padrão que permite a um
          assistente de IA descobrir e usar ferramentas de outros sistemas. Na
          Talkvia, essa conexão dá acesso às operações bancárias
          disponibilizadas pelo servidor MCP.
        </p>
        <p>
          Pense em um atendente com acesso a um balcão de serviços: ele recebe
          seu pedido, escolhe a ferramenta adequada e devolve o resultado. O MCP
          é a forma organizada de conversar com esse balcão; a IA é o assistente
          que interpreta o pedido.
        </p>
        <ol class="flow-grid" aria-label="Como o MCP funciona">
          <li>
            <strong>1. Você solicita</strong
            ><span>Descreve o que precisa ao assistente.</span>
          </li>
          <li>
            <strong>2. A IA usa uma ferramenta</strong
            ><span>Envia os dados exigidos pelo MCP.</span>
          </li>
          <li>
            <strong>3. O banco responde</strong
            ><span>A integração executa a operação disponível.</span>
          </li>
          <li>
            <strong>4. Você acompanha</strong
            ><span>O assistente explica o retorno e o próximo passo.</span>
          </li>
        </ol>
        <p>
          O MCP não é um banco nem um modelo de IA. Ele conecta o aplicativo
          escolhido às ferramentas; as regras e decisões de crédito continuam
          sendo de cada instituição.
        </p>
        <a
          class="text-n-brand underline"
          href="https://modelcontextprotocol.io/docs/getting-started/intro"
          target="_blank"
          rel="noopener noreferrer"
          >Conheça o padrão MCP na documentação oficial</a
        >
      </div>
    </details>
    <details class="guide-section">
      <summary>Benefícios para o dia a dia</summary>
      <div class="guide-content">
        <div class="grid gap-4 sm:grid-cols-2">
          <article>
            <h4>Menos tarefas repetitivas</h4>
            <p>
              Um fluxo bem configurado pode reaproveitar os dados já informados
              e reduzir a necessidade de copiar informações entre etapas.
            </p>
          </article>
          <article>
            <h4>Atendimento mais orientado</h4>
            <p>
              O assistente pode explicar o retorno da integração e indicar os
              dados ou a autorização necessários para continuar.
            </p>
          </article>
          <article>
            <h4>Integração mais padronizada</h4>
            <p>
              Aplicativos compatíveis descobrem as ferramentas pelo mesmo
              protocolo, o que pode reduzir adaptações específicas na conexão
              com a IA.
            </p>
          </article>
          <article>
            <h4>Mais continuidade no processo</h4>
            <p>
              Quando a operação estiver disponível, o assistente pode ajudar a
              acompanhar resultados e pendências dentro do mesmo atendimento.
            </p>
          </article>
        </div>
        <p>
          Essas melhorias dependem das ferramentas liberadas, da configuração do
          assistente e da disponibilidade dos bancos. A conexão não garante
          aprovação, resposta instantânea nem redução de custos.
        </p>
      </div>
    </details>
    <details class="guide-section">
      <summary>Quando usar cada integração?</summary>
      <div class="guide-content">
        <div class="overflow-x-auto">
          <table class="w-full text-left text-sm">
            <caption class="sr-only">
              Comparação entre API REST e MCP bancário
            </caption>
            <thead>
              <tr>
                <th>Seu objetivo</th>
                <th>Integração indicada</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Seu site ou CRM deve cadastrar um contato ou enviar uma
                  mensagem seguindo uma regra fixa.
                </td>
                <td>API REST: seu sistema chama um endpoint conhecido.</td>
              </tr>
              <tr>
                <td>
                  Um assistente deve descobrir ferramentas bancárias e usá-las
                  para atender a uma solicitação.
                </td>
                <td>
                  MCP: o aplicativo de IA se conecta ao catálogo de ferramentas.
                </td>
              </tr>
              <tr>
                <td>Você precisa das duas possibilidades na mesma operação.</td>
                <td>
                  REST e MCP podem coexistir. Cada conexão usa sua própria
                  configuração.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Os cinco endpoints da área convencional não se tornam automaticamente
          ferramentas MCP. O catálogo real do servidor informa o que seu
          assistente pode usar.
        </p>
      </div>
    </details>
    <details class="guide-section">
      <summary>Antes de começar</summary>
      <div class="guide-content">
        <ul>
          <li>
            Tenha um usuário Talkvia com acesso à conta e às operações
            desejadas.
          </li>
          <li>
            Confirme com o administrador quais bancos estão ativos e
            configurados para a empresa.
          </li>
          <li>
            Escolha um aplicativo de IA que aceite servidor MCP remoto por
            Streamable HTTP e cabeçalhos personalizados. Apenas ter um chat com
            IA não confirma essa compatibilidade.
          </li>
          <li>
            Tenha seu token pessoal, disponível em Meu perfil. Ele funciona como
            uma chave de acesso: informe-o apenas no campo de credenciais do
            aplicativo escolhido, nunca em uma conversa com a IA.
          </li>
        </ul>
        <p>
          Se você não administra essas configurações, peça à equipe técnica para
          fazer a conexão. Depois de pronta, o uso pode ocorrer por linguagem
          natural, conforme os recursos do aplicativo.
        </p>
      </div>
    </details>
    <details class="guide-section">
      <summary>Passo a passo para conectar</summary>
      <div class="guide-content">
        <ol>
          <li>
            <strong>Confira o ambiente.</strong> Homologação e produção são
            ambientes distintos. Use o usuário, token e ID de conta
            correspondentes ao ambiente mostrado abaixo.
          </li>
          <li>
            <strong>Abra as integrações do aplicativo de IA.</strong> Procure a
            opção de adicionar servidor MCP remoto. Os nomes dos menus variam
            conforme o aplicativo.
          </li>
          <li>
            <strong>Cadastre a conexão.</strong> Informe a URL do servidor e
            selecione Streamable HTTP, quando houver essa escolha.
          </li>
          <li>
            <strong>Configure a autenticação.</strong> Adicione Authorization
            com o valor Bearer seguido de um espaço e seu token pessoal.
            Adicione também X-Talkvia-Environment com o valor indicado na
            configuração.
          </li>
          <li>
            <strong>Salve e conecte.</strong> Se o aplicativo aceitar importação
            JSON, adapte o exemplo abaixo ao formato exigido por ele. Se não
            aceitar cabeçalhos personalizados, esta configuração não pode ser
            usada diretamente nele.
          </li>
          <li>
            <strong>Confira as ferramentas.</strong> Use a listagem de
            ferramentas do aplicativo. Esse primeiro teste deve apenas listar o
            catálogo, sem executar uma consulta bancária.
          </li>
          <li>
            <strong>Prepare o atendimento.</strong> Configure a conta atual e as
            orientações do assistente. Antes de uma operação real, obtenha a
            autorização aplicável do cliente e confirme os dados e a ação
            desejada.
          </li>
        </ol>
      </div>
    </details>
    <details ref="connectionDetails" class="guide-section">
      <summary>Dados da sua conexão</summary>
      <div class="guide-content">
        <template v-if="server.url">
          <dl class="grid gap-3 text-sm">
            <div>
              <dt class="font-medium">Servidor MCP · Streamable HTTP</dt>
              <dd class="mt-1 break-all">{{ server.url }}</dd>
            </div>
            <div>
              <dt class="font-medium">Ambiente</dt>
              <dd>
                {{
                  server.environment === 'homolog' ? 'Homologação' : 'Produção'
                }}
              </dd>
            </div>
            <div>
              <dt class="font-medium">Conta atual</dt>
              <dd>{{ accountId }}</dd>
            </div>
          </dl>
          <p class="text-sm text-n-slate-11">
            Em clientes compatíveis com URL e cabeçalhos HTTP, use a
            configuração abaixo. Substitua SEU_TOKEN_TALKVIA pelo token do seu
            perfil no cliente MCP. O formato de importação pode variar conforme
            o cliente.
          </p>
          <pre
            class="max-w-full overflow-x-auto rounded-lg bg-n-alpha-2 p-4 text-xs text-n-slate-12"
          ><code>{{ configuration }}</code></pre>
          <Button
            class="self-start"
            data-testid="mcp-copy-config"
            label="Copiar configuração MCP"
            icon="i-lucide-copy"
            @click="copyConfiguration"
          />
          <div class="text-sm text-n-slate-11">
            <p>
              Após conectar, consulte tools/list para obter as ferramentas e os
              parâmetros disponíveis. Nas operações bancárias, informe
              account_id com o ID da conta atual.
            </p>
            <p class="mt-2">
              O MCP usa Authorization: Bearer. O cabeçalho api_access_token
              apresentado nas outras seções pertence à API REST. Consultas
              bancárias continuam exigindo as autorizações do cliente aplicáveis
              a cada banco.
            </p>
          </div>
        </template>
        <p v-else class="text-sm text-n-slate-11">
          O servidor MCP bancário ainda não foi configurado neste ambiente.
          Solicite a configuração ao administrador da instalação.
        </p>
      </div>
    </details>
    <details class="guide-section">
      <summary>Exemplo de uso no atendimento</summary>
      <div class="guide-content">
        <p>
          Letícia Gonçalves já autorizou a consulta aplicável e os dados dela
          foram conferidos na Talkvia. Uma orientação para o assistente poderia
          ser:
        </p>
        <blockquote
          class="rounded-lg border-l-4 border-n-brand bg-n-alpha-2 p-4"
        >
          “Confira quais ferramentas estão disponíveis para a conta atual. Antes
          de consultar, confirme a autorização válida e os dados exigidos.
          Depois da minha confirmação, execute a operação disponível e explique
          o retorno sem prometer aprovação.”
        </blockquote>
        <p>
          Esse texto é uma orientação de exemplo, não um comando que esta página
          executa. O aplicativo precisa fornecer as ferramentas e o contexto
          correto ao assistente. Se faltar uma ferramenta para verificar a
          autorização, confirme-a pelo fluxo da Talkvia antes de prosseguir.
        </p>
        <p>
          Quando houver pendência, o assistente deve explicar o que falta;
          quando houver retorno do banco, deve apresentar esse retorno. Não deve
          inventar ofertas, taxas ou decisões.
        </p>
      </div>
    </details>
    <details class="guide-section rounded-lg border border-n-weak p-4">
      <summary class="cursor-pointer font-semibold text-n-slate-12">
        Detalhes técnicos para quem configura a integração
      </summary>
      <div class="mt-4 flex flex-col gap-3">
        <p>
          O cliente bancário da Talkvia utiliza o protocolo 2025-03-26, com
          mensagens JSON-RPC sobre HTTPS. O aplicativo precisa negociar uma
          versão aceita pelo servidor.
        </p>
        <ol>
          <li>
            Envie initialize com a versão de protocolo e a identificação do
            cliente.
          </li>
          <li>
            Guarde Mcp-Session-Id se o servidor devolvê-lo e envie esse
            cabeçalho nas requisições seguintes.
          </li>
          <li>
            Envie notifications/initialized e então tools/list para descobrir as
            ferramentas e seus esquemas de entrada.
          </li>
          <li>
            Use tools/call somente para a operação desejada, com os parâmetros
            exigidos pelo catálogo. Trate tanto erros HTTP quanto retornos MCP
            com isError.
          </li>
        </ol>
        <p>
          Inclua Content-Type: application/json e Accept: application/json,
          text/event-stream. O cliente deve interpretar respostas JSON e eventos
          SSE, quando usados pelo servidor. Uma URL aberta no navegador não
          realiza a negociação MCP.
        </p>
        <p>
          Na ferramenta banco_executar usada pela integração bancária da
          Talkvia, os argumentos incluem account_id, provider, operation e
          payload. Os valores válidos dependem do banco e da operação; consulte
          o esquema atual antes de montar uma chamada. Não reutilize exemplos de
          um banco em outro sem conferir os campos.
        </p>
        <pre
          class="max-w-full overflow-x-auto rounded-lg bg-n-alpha-2 p-4 text-xs"
        ><code>{"jsonrpc":"2.0","id":2,"method":"tools/list","params":{}}</code></pre>
        <p>
          O exemplo acima lista ferramentas depois da inicialização; ele não
          executa operações bancárias. Conectar ou copiar a configuração nesta
          página também não inicia uma consulta.
        </p>
        <a
          class="text-n-brand underline"
          href="https://modelcontextprotocol.io/specification/2025-03-26/basic/transports"
          target="_blank"
          rel="noopener noreferrer"
          >Consultar a especificação oficial do transporte MCP</a
        >
      </div>
    </details>
    <details class="guide-section">
      <summary>Dúvidas frequentes e solução de problemas</summary>
      <div class="guide-content">
        <details>
          <summary>Preciso saber programar?</summary>
          <p>
            Para usar um assistente já configurado, nem sempre. A primeira
            conexão pode exigir apoio técnico, principalmente se o aplicativo
            pedir cabeçalhos ou um arquivo JSON.
          </p>
        </details>
        <details>
          <summary>
            O token da Talkvia substitui as credenciais do banco?
          </summary>
          <p>
            Não. Ele identifica seu acesso à Talkvia. Os bancos precisam
            continuar ativos e com as credenciais necessárias configuradas na
            empresa.
          </p>
        </details>
        <details>
          <summary>Funciona com qualquer IA?</summary>
          <p>
            Depende do aplicativo, não apenas do modelo de IA. Verifique se ele
            suporta MCP remoto por Streamable HTTP e os cabeçalhos desta
            integração. Suporte a MCP por si só não garante compatibilidade com
            esta autenticação.
          </p>
        </details>
        <details>
          <summary>O que conferir quando aparece 401 ou 403?</summary>
          <p>
            Esses retornos podem indicar problema de autenticação ou acesso.
            Confira o token pessoal, o espaço após Bearer, o ambiente, a conta e
            as permissões do usuário. Não envie o token em mensagens ao suporte.
          </p>
        </details>
        <details>
          <summary>A conexão abre, mas não aparecem ferramentas.</summary>
          <p>
            Confira se a inicialização terminou e se o aplicativo solicitou
            tools/list. Verifique as permissões e o catálogo com o
            administrador. Não presuma que todo endpoint REST está disponível no
            MCP.
          </p>
        </details>
        <details>
          <summary>O banco não aparece ou pede autorização.</summary>
          <p>
            Confira se a integração está ativa, configurada e disponível para a
            operação. Se houver exigência de autorização, conclua o fluxo
            oficial com o cliente antes de continuar; o MCP não substitui esse
            aceite.
          </p>
        </details>
        <details>
          <summary>Houve demora, falha de rede ou sessão expirada.</summary>
          <p>
            Confira a disponibilidade do servidor e do banco. Reconecte se a
            sessão expirou. Antes de repetir uma operação que cria ou altera
            registros, verifique o resultado anterior para evitar duplicidade.
          </p>
        </details>
        <details>
          <summary>Usar MCP elimina custos ou limites?</summary>
          <p>
            Não. Continuam valendo o plano da Talkvia, os limites e condições
            dos bancos e os eventuais custos do aplicativo de IA. Confirme as
            condições de cada serviço com os responsáveis.
          </p>
        </details>
      </div>
    </details>
    <details class="guide-section">
      <summary>Pequeno glossário</summary>
      <div class="guide-content">
        <dl class="grid gap-3 sm:grid-cols-2">
          <div>
            <dt>Cliente MCP</dt>
            <dd>
              O aplicativo que faz a conexão, como um assistente ou uma
              ferramenta de automação compatível.
            </dd>
          </div>
          <div>
            <dt>Servidor MCP</dt>
            <dd>
              O serviço que publica as ferramentas e recebe os pedidos de
              execução.
            </dd>
          </div>
          <div>
            <dt>Ferramenta</dt>
            <dd>
              Uma operação disponível com nome, descrição e campos necessários.
            </dd>
          </div>
          <div>
            <dt>Token e cabeçalho</dt>
            <dd>
              O token é sua chave de acesso; o cabeçalho leva essa chave e
              outras informações junto de cada pedido.
            </dd>
          </div>
          <div>
            <dt>Conta / account_id</dt>
            <dd>
              O identificador da empresa na Talkvia. Ele não substitui o token
              nem concede permissão por si só.
            </dd>
          </div>
          <div>
            <dt>Homologação e produção</dt>
            <dd>
              Ambientes separados. Homologação serve à validação; confirme quais
              integrações externas estão conectadas antes de executar operações
              reais nela.
            </dd>
          </div>
        </dl>
      </div>
    </details>
  </section>
</template>

<style scoped>
.guide-content {
  @apply flex min-w-0 flex-col gap-3 pb-4;
}
.guide-section {
  @apply min-w-0 rounded-xl border border-n-weak px-4 text-sm text-n-slate-11;
}
h3 {
  @apply text-base font-semibold text-n-slate-12;
}
h4,
dt {
  @apply font-semibold text-n-slate-12;
}
ol {
  @apply list-decimal space-y-3 pl-5;
}
ul {
  @apply list-disc space-y-2 pl-5;
}
th,
td {
  @apply border-b border-n-weak p-3 align-top;
}
.flow-grid {
  @apply grid list-none gap-3 space-y-0 p-0 sm:grid-cols-2 lg:grid-cols-4;
}
.flow-grid li {
  @apply flex flex-col gap-2 rounded-lg bg-n-alpha-2 p-3;
}
summary {
  @apply cursor-pointer py-2 font-medium text-n-slate-12;
}
details p {
  @apply py-2;
}
</style>
